import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Skeleton } from 'antd'
import { useTranslation } from 'react-i18next'

import { useMutation } from 'services/query'
import { enterExternalMeetingRequest, hideModalAction, useAppDispatch } from 'store'
import { Button } from 'App/components'

import { createExternalMeeting, TCreateExternalMeetingResponse } from '../../../api'
import { InvitationLinkField } from '../../components/common/Fields/InvitationLinkField'

type TExternalMeetingLinkProps = {
  onCreate?: (meeting: TCreateExternalMeetingResponse) => void

  canEnter?: boolean
}

export const ExternalMeetingLink = ({ onCreate, canEnter }: TExternalMeetingLinkProps) => {
  const dispatch = useAppDispatch()
  const [meeting, setMeeting] = useState<TCreateExternalMeetingResponse | null>(null)
  const hasRequestedRef = useRef(false)

  const { t } = useTranslation()

  const { mutate } = useMutation({
    mutationKey: ['create-external-meeting'],
    mutationFn: createExternalMeeting,
    onSuccess: (created: TCreateExternalMeetingResponse) => {
      setMeeting(created)
      onCreate?.(created)
    }
  })

  useEffect(() => {
    if (hasRequestedRef.current) {
      return
    }

    hasRequestedRef.current = true
    mutate()
  }, [mutate])

  const handleEnter = () => {
    if (!meeting) {
      return
    }

    dispatch(enterExternalMeetingRequest({ admission: meeting.admission }))
    dispatch(hideModalAction())
  }

  return (
    <Skeleton active loading={!meeting} avatar={false} title paragraph={false}>
      <InvitationLinkField value={meeting?.joinUrl ?? ''} />

      {canEnter && (
        <Button onClick={handleEnter} variant="underlined" adaptive={false}>
          {t('modal.meetings.joinButton')}
        </Button>
      )}
    </Skeleton>
  )
}

export const useExternalMeeting = () => {
  const [meeting, setMeeting] = useState<TCreateExternalMeetingResponse | null>(null)

  const MeetingLink = useMemo<FC>(() => {
    const Comp: FC = () => <ExternalMeetingLink onCreate={setMeeting} />
    Comp.displayName = 'ExternalMeetingLink'

    return Comp
  }, [setMeeting])

  return {
    meeting,
    meetingId: meeting?.id ?? null,
    MeetingLink
  }
}
